import * as amplitude from '@amplitude/analytics-browser';
import { init as engagementInit } from '@amplitude/engagement-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { Injectable } from '@angular/core';

import { Customer } from '../../../models';

@Injectable({
  providedIn: 'root'
})
export class AmplitudeService {
  private engagementBooted = false;
  private engagementBootPromise?: Promise<void>;

  init(amplitudeApiKey: string, clientType: 'web' | 'app') {
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('client_type', clientType);

    amplitude.identify(identifyEvent);

    amplitude.add(sessionReplayPlugin());

    amplitude.init(amplitudeApiKey, {
      autocapture: {
        elementInteractions: true
      },
      serverUrl: 'https://data.europe.inyova.cloud/2/httpapi'
    });

    engagementInit(amplitudeApiKey, { serverZone: 'US' });
  }

  reset() {
    if (this.engagementBooted) {
      window.engagement.shutdown();
    }

    this.engagementBooted = false;
    this.engagementBootPromise = undefined;
    amplitude.reset();
  }

  async setUserProperties(customer: Customer) {
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('displayed_country', customer.app_location);
    identifyEvent.set('displayed_language', customer.language);
    identifyEvent.set('created_at', new Date(customer.created_at).toISOString());
    identifyEvent.set('is_test', customer.is_test);

    amplitude.setUserId(customer.track_id);
    amplitude.identify(identifyEvent);

    if (!this.engagementBooted && !this.engagementBootPromise && typeof window !== 'undefined') {
      this.engagementBootPromise = window.engagement
        .boot({
          user: {
            user_id: customer.track_id,
            user_properties: {
              displayed_country: customer.app_location,
              displayed_language: customer.language,
              is_test: customer.is_test
            }
          },
          integrations: [
            {
              track: (event) => {
                amplitude.track(event.event_type, event.event_properties);
              }
            }
          ]
        })
        .then(() => {
          this.engagementBooted = true;
        })
        .finally(() => {
          this.engagementBootPromise = undefined;
        });
    }
    await this.engagementBootPromise;
  }

  updateUserProperties(props: { propName: string; propValue: any }[]) {
    const identifyEvent = new amplitude.Identify();

    for (const prop of props) {
      identifyEvent.set(prop.propName, prop.propValue);
    }

    amplitude.identify(identifyEvent);
  }

  setDisplayedLanguage(language: string) {
    const identifyEvent = new amplitude.Identify();
    identifyEvent.set('displayed_language', language);

    amplitude.identify(identifyEvent);
  }

  trackEvent(eventName: string, eventProps?: any) {
    amplitude.track(eventName, eventProps);
  }
}
